/* =============================================================================
 *  TerminalInvoiceModal
 *
 *  Terminal-native replacement for `InvoiceViewModal`. Drop-in prop-compatible
 *  so hosts can swap based on theme without other changes. Same:
 *   - data surface (useInvoice / RecordPaymentModal / PaymentHistory)
 *   - feature set (void / email / copy link / copy-to-new / edit / record
 *     payment / view+download PDF)
 *   - status-based action gating
 *
 *  Differences from the classic:
 *   - Monospace body, dashed dividers, bracketed-text actions ([edit],
 *     [email], [copy-link], [copy-to-new], [record-payment], [void])
 *   - Prompt-style heading: `$ invoice INV-44492 [awaiting-payment]`
 *   - Amount block uses key/value lines instead of tiles
 *   - Line items table uses CSS grid with dashed separators
 *   - Status chip styles come from terminal palette (no Bootstrap colours)
 *
 *  Embedded children (RecordPaymentModal, PaymentHistory, InvoicePdfActions)
 *  remain the classic shared components — they carry their own visual style
 *  and are stable/tested. The surrounding `.term-invoice` scope tones them
 *  down enough to blend without a full rebuild of each.
 * ============================================================================= */

import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useInvoice } from '../contexts/InvoiceContext';
import { Invoice, INVOICE_STATUS_CONFIG } from '../../../types/accounting';
import { InvoicePdfActions } from '../../../components/InvoicePdfActions';
import { RecordPaymentModal } from './RecordPaymentModal';
import { PaymentHistory } from './PaymentHistory';
import { formatDateAU, isDateOverdue } from '../../../utils/dateUtils';
import './TerminalInvoiceModal.scss';

interface TerminalInvoiceModalProps {
  show: boolean;
  invoice: Invoice | null;
  onHide: () => void;
  onVoided?: () => void;
  /** Duplicate as new draft via CreateInvoiceModal(templateInvoice=...) */
  onCopyToNew?: (invoice: Invoice) => void;
  /** Edit via CreateInvoiceModal(editInvoice=...) */
  onEdit?: (invoice: Invoice) => void;
}

// Compact status slug used by the terminal header chip — lowercase,
// hyphenated. Status slug colours are applied via SCSS modifier class
// rather than inline styles.
function statusSlug(s?: string): string {
  return (s || 'draft').toLowerCase();
}

export function TerminalInvoiceModal({
  show, invoice, onHide, onVoided, onCopyToNew, onEdit,
}: TerminalInvoiceModalProps) {
  const {
    voidInvoice, emailInvoice, getOnlineInvoiceUrl, invoiceActionLoading,
    fetchPayments, deletePayment, paymentsLoading, deletePaymentLoading,
    recordPayment, recordPaymentLoading,
  } = useInvoice();

  const [onlineUrl, setOnlineUrl]       = useState<string | null>(null);
  const [actionError, setActionError]   = useState<string | null>(null);
  const [copySuccess, setCopySuccess]   = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [paymentReloadKey, setPaymentReloadKey] = useState(0); // bump → PaymentHistory re-fetch

  const fmtMoney = (amount: number, currency = 'AUD') =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount);

  const overdueCheck = (inv: Invoice) => {
    if ((inv.status !== 'APPROVED' && inv.status !== 'SENT') || !inv.amountDue) return false;
    return isDateOverdue(inv.dueDate);
  };

  const handleVoid = async () => {
    if (!invoice) return;
    setActionError(null);
    try {
      await voidInvoice(invoice.id);
      onVoided?.();
      onHide();
    } catch (err: any) {
      setActionError(err?.message || 'Failed to void invoice');
    }
  };

  const handleEmail = async () => {
    if (!invoice) return;
    setActionError(null);
    try {
      const { to } = await emailInvoice(invoice.id);
      toast.success(`Drafted an email${to ? ` to ${to}` : ''} with the invoice attached — review & send it from your Drafts.`);
    } catch (err: any) {
      setActionError(err?.message || 'Failed to draft the invoice email');
    }
  };

  const handleCopyLink = async () => {
    if (!invoice) return;
    setActionError(null);
    setCopySuccess(false);
    try {
      const url = await getOnlineInvoiceUrl(invoice.id);
      if (url) {
        setOnlineUrl(url);
        await navigator.clipboard.writeText(url);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } else {
        setActionError('Online invoice URL not available');
      }
    } catch (err: any) {
      setActionError(err?.message || 'Failed to get link');
    }
  };

  if (!invoice) return null;

  const overdue = overdueCheck(invoice);
  // Status-based gating — mirrors the classic modal. PAID + VOIDED are
  // frozen; everything else (DRAFT/SUBMITTED/AUTHORISED/APPROVED/SENT)
  // is editable.
  const canEdit          = invoice.status !== 'PAID' && invoice.status !== 'VOIDED';
  const canVoid          = invoice.status === 'APPROVED' || invoice.status === 'SENT';
  const canEmail         = invoice.status !== 'PAID' && invoice.status !== 'VOIDED';
  const canCopyLink      = invoice.status === 'APPROVED' || invoice.status === 'PAID';
  const canRecordPayment = (invoice.status === 'APPROVED' || invoice.status === 'SENT')
                           && (invoice.amountDue || 0) > 0;
  const isLoading = invoiceActionLoading === invoice.id;

  const statusLabel = INVOICE_STATUS_CONFIG[invoice.status]?.label || invoice.status || 'DRAFT';
  const slug = statusSlug(invoice.status);
  const amountDue = invoice.amountDue || 0;
  const amountPaid = invoice.amountPaid || 0;
  const currency = invoice.currencyCode || 'AUD';

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      fullscreen="md-down"
      dialogClassName="term-invoice-dialog"
      contentClassName="term-invoice-content"
    >
      <div className="term-invoice">
        {/* ── Header: prompt-style heading + status chip ────────────────── */}
        <div className="term-invoice__head">
          <div className="term-invoice__title">
            <span className="term-invoice__prompt">$</span>
            <span className="term-invoice__label">invoice</span>
            <span className="term-invoice__num">{invoice.invoiceNumber || `#${invoice.id.slice(0, 6)}`}</span>
            <span className={`term-invoice__status term-invoice__status--${slug}`}>
              [{slug}]
            </span>
            {overdue && (
              <span className="term-invoice__status term-invoice__status--overdue">
                [overdue]
              </span>
            )}
          </div>
          <button
            type="button"
            className="term-invoice__close"
            onClick={onHide}
            aria-label="Close"
            title="Close (Esc)"
          >
            [×]
          </button>
        </div>

        <div className="term-invoice__contact">
          <span className="term-invoice__key">contact:</span>
          <span className="term-invoice__val">{invoice.contactName || '(no customer)'}</span>
        </div>

        {/* ── Error line ─────────────────────────────────────────────────── */}
        {actionError && (
          <div className="term-invoice__error" role="alert">
            <span className="term-invoice__error-glyph">✗</span>
            <span>{actionError}</span>
          </div>
        )}

        {/* ── Totals block ───────────────────────────────────────────────── */}
        <div className="term-invoice__totals">
          <div className="term-invoice__total">
            <span className="term-invoice__kv-key">total</span>
            <span className="term-invoice__kv-val">{fmtMoney(invoice.total || 0, currency)}</span>
          </div>
          {amountDue > 0 && (
            <div className={
              'term-invoice__due'
              + (overdue ? ' term-invoice__due--overdue' : '')
            }>
              <span className="term-invoice__kv-key">amount due</span>
              <span className="term-invoice__kv-val">{fmtMoney(amountDue, currency)}</span>
            </div>
          )}
          {amountDue === 0 && invoice.status === 'PAID' && (
            <div className="term-invoice__due term-invoice__due--paid">
              <span className="term-invoice__kv-key">amount due</span>
              <span className="term-invoice__kv-val">{fmtMoney(0, currency)}</span>
            </div>
          )}
        </div>

        {amountPaid > 0 && (
          <div className="term-invoice__paid-line">
            <span className="term-invoice__key">paid:</span>
            <span className="term-invoice__val term-invoice__val--paid">{fmtMoney(amountPaid, currency)}</span>
          </div>
        )}

        {/* ── Meta: date / due-date / reference ──────────────────────────── */}
        <div className="term-invoice__meta">
          <div className="term-invoice__meta-cell">
            <span className="term-invoice__key">invoice date</span>
            <span className="term-invoice__val">{formatDateAU(invoice.date)}</span>
          </div>
          <div className="term-invoice__meta-cell">
            <span className="term-invoice__key">due date</span>
            <span className={
              'term-invoice__val'
              + (overdue ? ' term-invoice__val--overdue' : '')
            }>
              {formatDateAU(invoice.dueDate)}
              {overdue && <span className="term-invoice__bang"> !</span>}
            </span>
          </div>
          {invoice.reference && (
            <div className="term-invoice__meta-cell term-invoice__meta-cell--full">
              <span className="term-invoice__key">reference</span>
              <span className="term-invoice__val">{invoice.reference}</span>
            </div>
          )}
          {(onlineUrl || copySuccess) && (
            <div className="term-invoice__meta-cell term-invoice__meta-cell--full">
              <span className="term-invoice__key">online link</span>
              {onlineUrl
                ? <a href={onlineUrl} target="_blank" rel="noopener noreferrer" className="term-invoice__link">
                    {onlineUrl}
                  </a>
                : null}
              {copySuccess && <span className="term-invoice__copied">· copied</span>}
            </div>
          )}
        </div>

        {/* ── Line items ─────────────────────────────────────────────────── */}
        {invoice.lineItems && invoice.lineItems.length > 0 && (
          <div className="term-invoice__section">
            <div className="term-invoice__section-head">
              <span className="term-invoice__section-rule">── line items ──</span>
            </div>
            <div className="term-invoice__lines">
              <div className="term-invoice__lines-row term-invoice__lines-row--head">
                <span className="term-invoice__lines-desc">description</span>
                <span className="term-invoice__lines-qty">qty</span>
                <span className="term-invoice__lines-unit">unit price</span>
                <span className="term-invoice__lines-amt">amount</span>
              </div>
              {invoice.lineItems.map((item, idx) => (
                <div key={idx} className="term-invoice__lines-row">
                  <span className="term-invoice__lines-desc">{item.description || '—'}</span>
                  <span className="term-invoice__lines-qty">{item.quantity || 1}</span>
                  <span className="term-invoice__lines-unit">{fmtMoney(item.unitAmount || 0, currency)}</span>
                  <span className="term-invoice__lines-amt">{fmtMoney(item.lineAmount || 0, currency)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Payment history (classic component — keeps its own style) ── */}
        <div className="term-invoice__section">
          <div className="term-invoice__section-head">
            <span className="term-invoice__section-rule">── payment history ──</span>
          </div>
          <div className="term-invoice__payments">
            <PaymentHistory
              documentId={invoice.id}
              currencyCode={invoice.currencyCode}
              onPaymentDeleted={() => setShowRecordPayment(false)}
              fetchPayments={fetchPayments}
              deletePayment={deletePayment}
              paymentsLoading={paymentsLoading}
              deletePaymentLoading={deletePaymentLoading}
              reloadKey={paymentReloadKey}
            />
          </div>
        </div>

        {/* ── Actions ────────────────────────────────────────────────────── */}
        <div className="term-invoice__actions">
          {onEdit && canEdit && (
            <button
              type="button"
              className="term-invoice__action term-invoice__action--accent"
              onClick={() => onEdit(invoice)}
              disabled={isLoading}
              title="Edit this invoice"
            >
              [edit]
            </button>
          )}
          {canRecordPayment && (
            <button
              type="button"
              className="term-invoice__action term-invoice__action--accent"
              onClick={() => setShowRecordPayment(true)}
              disabled={isLoading}
              title="Record a payment against this invoice"
            >
              [record-payment]
            </button>
          )}
          {canEmail && (
            <button
              type="button"
              className="term-invoice__action"
              onClick={handleEmail}
              disabled={isLoading}
              title="Email this invoice"
            >
              {isLoading ? '[emailing…]' : '[email]'}
            </button>
          )}
          {canCopyLink && (
            <button
              type="button"
              className="term-invoice__action"
              onClick={handleCopyLink}
              disabled={isLoading}
              title="Copy the public online-invoice URL"
            >
              {copySuccess ? '[link-copied]' : '[copy-link]'}
            </button>
          )}
          {onCopyToNew && (
            <button
              type="button"
              className="term-invoice__action"
              onClick={() => onCopyToNew(invoice)}
              disabled={isLoading}
              title="Duplicate this invoice as a new draft"
            >
              [copy-to-new]
            </button>
          )}
          {canVoid && (
            <button
              type="button"
              className="term-invoice__action term-invoice__action--danger"
              onClick={handleVoid}
              disabled={isLoading}
              title="Void this invoice"
            >
              [void]
            </button>
          )}

          {/* PDF cluster — right-aligned. InvoicePdfActions has its own
              chrome; scoped SCSS tones it down to match. */}
          <span className="term-invoice__actions-spacer" />
          <div className="term-invoice__pdf">
            <InvoicePdfActions
              invoiceId={invoice.id}
              invoiceNumber={invoice.invoiceNumber}
              size="sm"
            />
          </div>

          <button
            type="button"
            className="term-invoice__action term-invoice__action--dim"
            onClick={onHide}
            title="Close"
          >
            [close]
          </button>
        </div>
      </div>

      {/* Payment recording modal (unchanged shared component) */}
      <RecordPaymentModal
        show={showRecordPayment}
        documentId={invoice.id}
        documentNumber={invoice.invoiceNumber}
        amountDue={amountDue}
        currencyCode={invoice.currencyCode}
        onSuccess={() => setPaymentReloadKey((k) => k + 1)}
        onHide={() => setShowRecordPayment(false)}
        recordPayment={(p) => recordPayment({
          invoiceId: p.documentId, method: p.method, amount: p.amount,
          date: p.date, reference: p.reference,
        })}
        recordPaymentLoading={recordPaymentLoading}
      />
    </Modal>
  );
}

export default TerminalInvoiceModal;
